import { useState } from 'react';
import { Search, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { BoampSearchParams, BoampNature } from '../../types/opportunity.types';
import { DescriptorMultiSelect } from './DescriptorMultiSelect';

// Liste complète des départements français
const DEPARTMENTS = [
  { code: '01', name: 'Ain' },
  { code: '02', name: 'Aisne' },
  { code: '03', name: 'Allier' },
  { code: '04', name: 'Alpes-de-Haute-Provence' },
  { code: '05', name: 'Hautes-Alpes' },
  { code: '06', name: 'Alpes-Maritimes' },
  { code: '07', name: 'Ardèche' },
  { code: '08', name: 'Ardennes' },
  { code: '09', name: 'Ariège' },
  { code: '10', name: 'Aube' },
  { code: '11', name: 'Aude' },
  { code: '12', name: 'Aveyron' },
  { code: '13', name: 'Bouches-du-Rhône' },
  { code: '14', name: 'Calvados' },
  { code: '15', name: 'Cantal' },
  { code: '16', name: 'Charente' },
  { code: '17', name: 'Charente-Maritime' },
  { code: '18', name: 'Cher' },
  { code: '19', name: 'Corrèze' },
  { code: '2A', name: 'Corse-du-Sud' },
  { code: '2B', name: 'Haute-Corse' },
  { code: '21', name: "Côte-d'Or" },
  { code: '22', name: "Côtes-d'Armor" },
  { code: '23', name: 'Creuse' },
  { code: '24', name: 'Dordogne' },
  { code: '25', name: 'Doubs' },
  { code: '26', name: 'Drôme' },
  { code: '27', name: 'Eure' },
  { code: '28', name: 'Eure-et-Loir' },
  { code: '29', name: 'Finistère' },
  { code: '30', name: 'Gard' },
  { code: '31', name: 'Haute-Garonne' },
  { code: '32', name: 'Gers' },
  { code: '33', name: 'Gironde' },
  { code: '34', name: 'Hérault' },
  { code: '35', name: 'Ille-et-Vilaine' },
  { code: '36', name: 'Indre' },
  { code: '37', name: 'Indre-et-Loire' },
  { code: '38', name: 'Isère' },
  { code: '39', name: 'Jura' },
  { code: '40', name: 'Landes' },
  { code: '41', name: 'Loir-et-Cher' },
  { code: '42', name: 'Loire' },
  { code: '43', name: 'Haute-Loire' },
  { code: '44', name: 'Loire-Atlantique' },
  { code: '45', name: 'Loiret' },
  { code: '46', name: 'Lot' },
  { code: '47', name: 'Lot-et-Garonne' },
  { code: '48', name: 'Lozère' },
  { code: '49', name: 'Maine-et-Loire' },
  { code: '50', name: 'Manche' },
  { code: '51', name: 'Marne' },
  { code: '52', name: 'Haute-Marne' },
  { code: '53', name: 'Mayenne' },
  { code: '54', name: 'Meurthe-et-Moselle' },
  { code: '55', name: 'Meuse' },
  { code: '56', name: 'Morbihan' },
  { code: '57', name: 'Moselle' },
  { code: '58', name: 'Nièvre' },
  { code: '59', name: 'Nord' },
  { code: '60', name: 'Oise' },
  { code: '61', name: 'Orne' },
  { code: '62', name: 'Pas-de-Calais' },
  { code: '63', name: 'Puy-de-Dôme' },
  { code: '64', name: 'Pyrénées-Atlantiques' },
  { code: '65', name: 'Hautes-Pyrénées' },
  { code: '66', name: 'Pyrénées-Orientales' },
  { code: '67', name: 'Bas-Rhin' },
  { code: '68', name: 'Haut-Rhin' },
  { code: '69', name: 'Rhône' },
  { code: '70', name: 'Haute-Saône' },
  { code: '71', name: 'Saône-et-Loire' },
  { code: '72', name: 'Sarthe' },
  { code: '73', name: 'Savoie' },
  { code: '74', name: 'Haute-Savoie' },
  { code: '75', name: 'Paris' },
  { code: '76', name: 'Seine-Maritime' },
  { code: '77', name: 'Seine-et-Marne' },
  { code: '78', name: 'Yvelines' },
  { code: '79', name: 'Deux-Sèvres' },
  { code: '80', name: 'Somme' },
  { code: '81', name: 'Tarn' },
  { code: '82', name: 'Tarn-et-Garonne' },
  { code: '83', name: 'Var' },
  { code: '84', name: 'Vaucluse' },
  { code: '85', name: 'Vendée' },
  { code: '86', name: 'Vienne' },
  { code: '87', name: 'Haute-Vienne' },
  { code: '88', name: 'Vosges' },
  { code: '89', name: 'Yonne' },
  { code: '90', name: 'Territoire de Belfort' },
  { code: '91', name: 'Essonne' },
  { code: '92', name: 'Hauts-de-Seine' },
  { code: '93', name: 'Seine-Saint-Denis' },
  { code: '94', name: 'Val-de-Marne' },
  { code: '95', name: "Val-d'Oise" },
  { code: '971', name: 'Guadeloupe' },
  { code: '972', name: 'Martinique' },
  { code: '973', name: 'Guyane' },
  { code: '974', name: 'La Réunion' },
  { code: '976', name: 'Mayotte' },
];

// Natures d'avis
const NATURES: { value: BoampNature; label: string }[] = [
  { value: 'APPEL_OFFRE', label: "Appel d'offres" },
  { value: 'AVIS_ATTRIBUTION', label: "Avis d'attribution" },
  { value: 'AVIS_RECTIFICATIF', label: 'Avis rectificatif' },
  { value: 'ANNULATION', label: 'Annulation' },
  { value: 'MISE_AU_POINT', label: 'Mise au point' },
];

interface BoampSearchFiltersProps {
  onSearch: (params: BoampSearchParams) => void;
  isLoading?: boolean;
}

export function BoampSearchFilters({
  onSearch,
  isLoading,
}: BoampSearchFiltersProps) {
  // Filtres principaux
  const [idweb, setAnnouncementId] = useState('');
  const [descriptors, setDescriptors] = useState<string[]>([]);
  const [buyerName, setBuyerName] = useState('');
  const [marketTypes, setMarketTypes] = useState<string[]>([]);

  // Filtres avancés
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [nature, setNature] = useState<string>('APPEL_OFFRE');
  const [department, setDepartment] = useState<string>('all');
  const [publicationAfter, setPublicationAfter] = useState('');
  const [publicationBefore, setPublicationBefore] = useState('');
  const [deadlineAfter, setDeadlineAfter] = useState('');
  const [deadlineBefore, setDeadlineBefore] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  const handleSearch = () => {
    const params: BoampSearchParams = {
      idweb: idweb || undefined,
      descriptors: descriptors.length > 0 ? descriptors : undefined,
      buyerName: buyerName || undefined,
      marketTypes:
        marketTypes.length > 0
          ? (marketTypes as ('SERVICES' | 'TRAVAUX' | 'FOURNITURES')[])
          : undefined,
      nature: nature !== 'all' ? (nature as BoampNature) : undefined,
      departments:
        department && department !== 'all' ? [department] : undefined,
      publicationAfter: publicationAfter || undefined,
      publicationBefore: publicationBefore || undefined,
      deadlineAfter: deadlineAfter || undefined,
      deadlineBefore: deadlineBefore || undefined,
      amountMin: amountMin ? Number(amountMin) : undefined,
      amountMax: amountMax ? Number(amountMax) : undefined,
      page: 1,
      pageSize: 20,
      sortBy: 'dateparution',
      sortOrder: 'desc',
    };
    onSearch(params);
  };

  const handleReset = () => {
    setAnnouncementId('');
    setDescriptors([]);
    setBuyerName('');
    setMarketTypes([]);
    setNature('APPEL_OFFRE');
    setDepartment('all');
    setPublicationAfter('');
    setPublicationBefore('');
    setDeadlineAfter('');
    setDeadlineBefore('');
    setAmountMin('');
    setAmountMax('');
    onSearch({ page: 1, pageSize: 20, sortBy: 'dateparution', sortOrder: 'desc' });
  };

  const toggleMarketType = (type: string) => {
    setMarketTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="bg-surface rounded-lg border p-6 space-y-4">
      {/* Filtres principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Numéro d'annonce */}
        <div className="space-y-2">
          <Label htmlFor="announcementId">N° d'annonce</Label>
          <Input
            id="announcementId"
            placeholder="Ex: 26-12345"
            value={idweb}
            onChange={(e) => setAnnouncementId(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Mots-clés (descripteurs BOAMP) */}
        <div className="space-y-2 lg:col-span-2">
          <Label>Mots-clés</Label>
          <DescriptorMultiSelect
            value={descriptors}
            onChange={setDescriptors}
            placeholder="Sélectionner des mots-clés..."
          />
        </div>

        {/* Nom acheteur */}
        <div className="space-y-2">
          <Label htmlFor="buyerName">Nom de l'acheteur</Label>
          <Input
            id="buyerName"
            placeholder="Ex: Ville de Paris"
            value={buyerName}
            onChange={(e) => setBuyerName(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Département */}
        <div className="space-y-2">
          <Label htmlFor="department">Département</Label>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger>
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">Tous les départements</SelectItem>
              {DEPARTMENTS.map((dept) => (
                <SelectItem key={dept.code} value={dept.code}>
                  {dept.code} - {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Types de marché */}
      <div className="space-y-2">
        <Label>Type de marché</Label>
        <div className="flex flex-wrap gap-2">
          {['SERVICES', 'TRAVAUX', 'FOURNITURES'].map((type) => (
            <Button
              key={type}
              type="button"
              variant={marketTypes.includes(type) ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleMarketType(type)}
              className={
                marketTypes.includes(type) ? 'bg-copper hover:bg-copper/90' : ''
              }
            >
              {type.charAt(0) + type.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
      </div>

      {/* Filtres avancés (collapsible) */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-between">
            Filtres avancés
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4 ml-2" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-2" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          {/* Nature d'avis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nature">Nature de l'avis</Label>
              <Select value={nature} onValueChange={setNature}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les natures</SelectItem>
                  {NATURES.map((n) => (
                    <SelectItem key={n.value} value={n.value}>
                      {n.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dates de parution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="publicationAfter">Publiés depuis le</Label>
              <Input
                id="publicationAfter"
                type="date"
                value={publicationAfter}
                onChange={(e) => setPublicationAfter(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="publicationBefore">Publiés jusqu'au</Label>
              <Input
                id="publicationBefore"
                type="date"
                value={publicationBefore}
                onChange={(e) => setPublicationBefore(e.target.value)}
              />
            </div>
          </div>

          {/* Dates limites de réponse */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deadlineAfter">Date limite à partir du</Label>
              <Input
                id="deadlineAfter"
                type="date"
                value={deadlineAfter}
                onChange={(e) => setDeadlineAfter(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="deadlineBefore">Date limite jusqu'au</Label>
              <Input
                id="deadlineBefore"
                type="date"
                value={deadlineBefore}
                onChange={(e) => setDeadlineBefore(e.target.value)}
              />
            </div>
          </div>

          {/* Montants */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amountMin">Valeur minimale (EUR)</Label>
              <Input
                id="amountMin"
                type="number"
                placeholder="Ex: 10000"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
                onKeyDown={handleKeyDown}
                min={0}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="amountMax">Valeur maximale (EUR)</Label>
              <Input
                id="amountMax"
                type="number"
                placeholder="Ex: 500000"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
                onKeyDown={handleKeyDown}
                min={0}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSearch} disabled={isLoading} className="flex-1">
          <Search className="w-4 h-4 mr-2" />
          Rechercher
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={isLoading}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Réinitialiser
        </Button>
      </div>
    </div>
  );
}
